import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Tabs, Tab, Chip, Button, Typography } from '@mui/material';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import SchemaTab from './SchemaTab';
import NotesTab from './NotesTab';
import { providerKey, htmlEquals } from './constants';
import { getIntegrationHub, saveClientDoc, currentUserName } from '../../../services/integrationHubService';

const SAVE_DEBOUNCE_MS = 1500;
const EMPTY_DOC = { notesHtml: '', links: [] };

const TABS = [
    { id: 'schema', label: 'Schema', icon: <TableChartOutlinedIcon sx={{ fontSize: 16 }} /> },
    { id: 'notes', label: 'Notes', icon: <StickyNote2OutlinedIcon sx={{ fontSize: 16 }} /> },
];

// Everything for one client, inside one provider's page: just this client's
// own Schema (live segments + "how we connect for this client") and general
// Notes — one row per client, fetched/saved right here since no other
// client's card needs it. Contacts/Notes/Links/Files for the provider itself
// live one level up, on ProviderInfoCard — they're the same for every client
// on this provider, not this client's own thing.

const ClientWorkspace = ({ providerId, providerName, client, onChange }) => {
    const [tab, setTab] = useState('schema');
    const [doc, setDoc] = useState(EMPTY_DOC);
    const [updatedOn, setUpdatedOn] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saveState, setSaveState] = useState('idle');
    const [dirty, setDirty] = useState(false);
    const [error, setError] = useState(null);

    const docRef = useRef(doc);
    docRef.current = doc;
    const updatedOnRef = useRef(null);
    updatedOnRef.current = updatedOn;
    const saveTimer = useRef(null);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        getIntegrationHub(client.clientId)
            .then((hub) => {
                if (!alive) return;
                setDoc({
                    notesHtml: hub.document?.notesHtml || '',
                    links: hub.document?.links || [],
                });
                setUpdatedOn(hub.document?.updatedOn || null);
            })
            .catch((e) => { if (alive) setError(e.message); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [client.clientId]);

    const persist = useCallback(async () => {
        setSaveState('saving');
        try {
            const who = await currentUserName();
            const res = await saveClientDoc(client.clientId, docRef.current, who, updatedOnRef.current);
            setUpdatedOn(res.updatedOn || null);
            setDirty(false);
            setSaveState('saved');
        } catch (e) {
            setError(e.message);
            setSaveState('error');
        }
    }, [client.clientId]);

    const updateDoc = useCallback((next) => {
        const current = docRef.current;
        const same = htmlEquals(next.notesHtml, current.notesHtml)
            && JSON.stringify(next.links || []) === JSON.stringify(current.links || []);
        if (!same) {
            setDirty(true);
            clearTimeout(saveTimer.current);
            saveTimer.current = setTimeout(persist, SAVE_DEBOUNCE_MS);
        }
        setDoc(next);
    }, [persist]);

    const saveNow = () => {
        clearTimeout(saveTimer.current);
        persist();
    };

    useEffect(() => () => clearTimeout(saveTimer.current), []);

    if (loading) {
        return <Typography sx={{ fontSize: 12.5, color: 'text.secondary', py: 2 }}>Loading…</Typography>;
    }

    return (
        <Box>
            {error && <Typography sx={{ color: '#c62828', fontSize: 12.5, mb: 1 }}>{error}</Typography>}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Tabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    sx={{
                        minHeight: 36,
                        flex: 1,
                        '& .MuiTab-root': { textTransform: 'none', minHeight: 36, fontSize: 12.5, fontWeight: 600, gap: 0.6, minWidth: 0, px: 1.5 },
                        '& .Mui-selected': { color: '#004df2' },
                    }}
                    TabIndicatorProps={{ style: { backgroundColor: '#004df2', height: 2, borderRadius: 2 } }}
                >
                    {TABS.map((t) => (
                        <Tab key={t.id} value={t.id} label={t.label} iconPosition="start" icon={t.icon} />
                    ))}
                </Tabs>
                {saveState !== 'idle' && (
                    <Chip
                        size="small"
                        label={saveState === 'saving' ? 'Saving…' : saveState === 'error' ? 'Not saved' : 'Saved'}
                        color={saveState === 'error' ? 'error' : saveState === 'saved' ? 'success' : 'default'}
                        variant={saveState === 'saving' ? 'outlined' : 'filled'}
                        sx={{ fontWeight: 600, fontSize: 10.5, height: 20, mr: 1 }}
                    />
                )}
                <Button size="small" onClick={saveNow} disabled={!dirty || saveState === 'saving'}
                    sx={{ textTransform: 'none', fontSize: 11.5, color: '#004df2', minWidth: 0, py: 0.25 }}>
                    Save
                </Button>
            </Box>

            {tab === 'schema' && (
                <SchemaTab
                    clientId={client.clientId}
                    matchKey={providerKey(providerName)}
                    schema={client.schema}
                    onSchemaChange={(schema) => onChange({ ...client, schema })}
                />
            )}

            {tab === 'notes' && <NotesTab doc={doc} onDocChange={updateDoc} />}
        </Box>
    );
};

export default ClientWorkspace;
